import React, { useState, useEffect } from 'react';
import { Lock, Unlock, BookOpen, Code, Users, ChevronDown, ChevronRight, RefreshCw, CheckCircle, AlertCircle, Settings, Zap, Layout, Database, Globe } from 'lucide-react';

// Use same auth as main app
const AUTH_STORAGE_KEY = 'adag_optimizer_auth';

function Documentation() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('simple');
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    // Check main app auth
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      if (stored) {
        const { authenticated, expiry } = JSON.parse(stored);
        if (authenticated && new Date(expiry) > new Date()) {
          setIsAuthenticated(true);
        }
      }
    } catch (e) {
      // Not authenticated
    }
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Zugang verweigert</h1>
          <p className="text-slate-500 mt-2">Bitte zuerst über das Hauptmenü anmelden.</p>
          <a 
            href="/"
            className="mt-6 inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition"
          >
            Zur Anmeldung
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">ADAG Optimizer - Dokumentation</h1>
                <p className="text-sm text-slate-500">Letzte Aktualisierung: März 2026</p>
              </div>
            </div>
            
            {/* Tab Switcher */}
            <div className="flex bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('simple')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition flex items-center gap-2 ${
                  activeTab === 'simple' 
                    ? 'bg-white text-emerald-600 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                <Users className="w-4 h-4" />
                Für Mitarbeiter
              </button>
              <button
                onClick={() => setActiveTab('technical')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition flex items-center gap-2 ${
                  activeTab === 'technical' 
                    ? 'bg-white text-emerald-600 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                <Code className="w-4 h-4" />
                Technisch
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {activeTab === 'simple' ? (
          <SimpleDocumentation expandedSections={expandedSections} toggleSection={toggleSection} />
        ) : (
          <TechnicalDocumentation expandedSections={expandedSections} toggleSection={toggleSection} />
        )}
      </div>
    </div>
  );
}

// Simple Documentation for Employees
function SimpleDocumentation({ expandedSections, toggleSection }) {
  return (
    <div className="space-y-6">
      {/* Overview Card */}
      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-8 text-white">
        <h2 className="text-2xl font-bold mb-3">Was ist der ADAG Optimizer?</h2>
        <p className="text-emerald-100 text-lg leading-relaxed">
          Der ADAG Optimizer ist unser internes Tool zur Verbesserung des Webshops. 
          Er analysiert die Website automatisch und schlägt Optimierungen vor, 
          die mehr Kunden zum Kauf bewegen sollen.
        </p>
      </div>

      {/* Main Features */}
      <div className="grid md:grid-cols-2 gap-6">
        <FeatureCard
          icon={<Zap className="w-6 h-6" />}
          title="Automatische Analyse"
          description="Das Tool scannt regelmässig unsere Kategorie-Seiten und findet Verbesserungspotenzial."
          color="amber"
        />
        <FeatureCard
          icon={<Layout className="w-6 h-6" />}
          title="Design-Vorschläge"
          description="Es erstellt neue Layouts für Kategorie-Seiten, die benutzerfreundlicher sind."
          color="blue"
        />
        <FeatureCard
          icon={<CheckCircle className="w-6 h-6" />}
          title="Genehmigungs-Workflow"
          description="Alle Änderungen müssen von Marco genehmigt werden, bevor sie live gehen."
          color="emerald"
        />
        <FeatureCard
          icon={<RefreshCw className="w-6 h-6" />}
          title="Direkte Umsetzung"
          description="Nach Genehmigung werden Änderungen automatisch auf der Website umgesetzt."
          color="purple"
        />
      </div>

      {/* What was done */}
      <Section title="Was wurde bisher umgesetzt?" defaultOpen={true}>
        <div className="space-y-4">
          <StatusItem 
            status="done" 
            title="Homepage Redesign" 
            description="Neues Layout mit klaren Produktkategorien und Express-Hinweisen"
          />
          <StatusItem 
            status="done" 
            title="Alle Kategorie-Seiten" 
            description="Einheitliches Design für Bindungen, Flyer, Visitenkarten, Poster, Kalender, etc."
          />
          <StatusItem 
            status="done" 
            title="Navigation vereinfacht" 
            description="Von 9 auf 6 Menüpunkte reduziert für bessere Übersicht"
          />
          <StatusItem 
            status="done" 
            title="Mobile Optimierung" 
            description="Alle Seiten funktionieren jetzt gut auf Smartphones"
          />
          <StatusItem 
            status="done" 
            title="Öffnungszeiten korrigiert" 
            description="Mo-Fr 9-12 & 13-18 Uhr auf allen Seiten"
          />
        </div>
      </Section>

      {/* Important Info */}
      <Section title="Wichtige Informationen">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h4 className="font-semibold text-amber-800 mb-3">Bitte beachten:</h4>
          <ul className="space-y-2 text-amber-700">
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-1">•</span>
              <span>Änderungen am Webshop werden nur durch Marco freigegeben</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-1">•</span>
              <span>Nach Änderungen kann es 5-10 Minuten dauern, bis sie sichtbar sind (Cache)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-1">•</span>
              <span>Bei Problemen mit der Website bitte Marco informieren</span>
            </li>
          </ul>
        </div>
      </Section>

      {/* Contact */}
      <div className="bg-slate-100 rounded-xl p-6 text-center">
        <p className="text-slate-600">
          Fragen zum Tool? Wende dich an <strong>Marco</strong> oder schreibe an{' '}
          <a href="mailto:zuerich@adagprint.ch" className="text-emerald-600 hover:underline">
            zuerich@adagprint.ch
          </a>
        </p>
      </div>
    </div>
  );
}

// Technical Documentation
function TechnicalDocumentation({ expandedSections, toggleSection }) {
  return (
    <div className="space-y-6">
      {/* Architecture Overview */}
      <div className="bg-slate-800 rounded-2xl p-8 text-white">
        <h2 className="text-2xl font-bold mb-3">Systemarchitektur</h2>
        <p className="text-slate-300 mb-6">
          Der ADAG Optimizer ist eine Full-Stack-Anwendung, die direkt mit dem Magento-Shop interagiert.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <TechCard title="Frontend" tech="React 18" description="UI Dashboard" />
          <TechCard title="Backend" tech="FastAPI (Python)" description="API & SSH-Steuerung" />
          <TechCard title="Ziel-System" tech="Magento 2" description="Live-Shop" />
        </div>
      </div>

      {/* Components */}
      <Section title="Komponenten-Übersicht" defaultOpen={true}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Komponente</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Pfad</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Funktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr><td className="py-3 px-4 font-medium">Optimizer</td><td className="py-3 px-4 text-slate-600">/optimizer</td><td className="py-3 px-4 text-slate-600">Hauptseite - Kategorie-Analyse & Vorschläge</td></tr>
              <tr><td className="py-3 px-4 font-medium">Designer</td><td className="py-3 px-4 text-slate-600">/designer</td><td className="py-3 px-4 text-slate-600">Visueller Editor für Layouts</td></tr>
              <tr><td className="py-3 px-4 font-medium">Dashboard</td><td className="py-3 px-4 text-slate-600">/dashboard</td><td className="py-3 px-4 text-slate-600">Übersicht & Statistiken</td></tr>
              <tr><td className="py-3 px-4 font-medium">Issues</td><td className="py-3 px-4 text-slate-600">/issues</td><td className="py-3 px-4 text-slate-600">Problemverfolgung</td></tr>
              <tr><td className="py-3 px-4 font-medium">Growth Engine</td><td className="py-3 px-4 text-slate-600">/growth</td><td className="py-3 px-4 text-slate-600">Wachstumsanalysen (geplant)</td></tr>
              <tr><td className="py-3 px-4 font-medium">Dokumentation</td><td className="py-3 px-4 text-slate-600">/docs</td><td className="py-3 px-4 text-slate-600">Diese Seite</td></tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* Magento Integration */}
      <Section title="Magento Integration">
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-semibold text-slate-800 mb-2">Verbindung</h4>
            <p className="text-slate-600 text-sm mb-3">Der Backend-Server verbindet sich via SSH zum Magento-Server und führt Datenbankänderungen durch.</p>
            <CodeBlock code={`Verbindung: SSH (Port 2121)
Credentials: Siehe Administrator`} />
          </div>
          
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-semibold text-slate-800 mb-2">Wichtige Tabellen</h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li><code className="bg-slate-200 px-1 rounded">cms_block</code> - CMS Inhalte (Landing Pages)</li>
              <li><code className="bg-slate-200 px-1 rounded">catalog_category_entity_*</code> - Kategorie-Attribute</li>
              <li><code className="bg-slate-200 px-1 rounded">ves_megamenu_item</code> - Navigation</li>
              <li><code className="bg-slate-200 px-1 rounded">url_rewrite</code> - URL-Zuordnungen</li>
            </ul>
          </div>

          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-semibold text-slate-800 mb-2">CMS Block IDs (Wichtigste)</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><code className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">106</code> Abschlussarbeiten</div>
              <div><code className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">95</code> Flyer/Karten</div>
              <div><code className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">87</code> Visitenkarten</div>
              <div><code className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">86</code> Plakate/Poster</div>
              <div><code className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">99</code> Kalender</div>
              <div><code className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">118</code> Roll-Up Banner</div>
              <div><code className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">138</code> Grafik</div>
              <div><code className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">143</code> Copyshop</div>
              <div><code className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">119</code> Kontakt</div>
              <div><code className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">140</code> Bindungen Bindeart</div>
              <div><code className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">141</code> Bindungen Vorschrift</div>
            </div>
          </div>
        </div>
      </Section>

      {/* Cache Management */}
      <Section title="Cache-Management">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-4">
          <h4 className="font-semibold text-amber-800 mb-2">Multi-Layer Caching</h4>
          <p className="text-amber-700 text-sm">Änderungen müssen durch mehrere Cache-Schichten propagieren:</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <h5 className="font-semibold text-slate-800">1. Magento Cache</h5>
            <p className="text-sm text-slate-600 mt-1">Wird via CLI geflusht</p>
            <CodeBlock code="php bin/magento cache:flush" />
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <h5 className="font-semibold text-slate-800">2. Nginx Cache</h5>
            <p className="text-sm text-slate-600 mt-1">Automatisch nach Magento</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <h5 className="font-semibold text-slate-800">3. Cloudflare CDN</h5>
            <p className="text-sm text-slate-600 mt-1">Manueller Purge nötig</p>
            <CodeBlock code="Dashboard > Cache > Purge" />
          </div>
        </div>
      </Section>

      {/* API Endpoints */}
      <Section title="API Endpoints">
        <div className="space-y-3">
          <APIEndpoint method="GET" path="/api/categories" description="Liste aller Kategorien mit CMS Blocks" />
          <APIEndpoint method="POST" path="/api/analyze" description="Startet Analyse einer Kategorie" />
          <APIEndpoint method="POST" path="/api/suggestions" description="Generiert AI-Vorschläge" />
          <APIEndpoint method="POST" path="/api/deploy" description="Deployed genehmigte Änderungen" />
          <APIEndpoint method="GET" path="/api/issues" description="Liste aller erkannten Probleme" />
        </div>
      </Section>

      {/* Credentials Location */}
      <Section title="Credentials & Konfiguration">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h4 className="font-semibold text-amber-800 mb-2">Hinweis</h4>
          <p className="text-sm text-amber-700">
            Credentials und Zugangsdaten sind zentral verwaltet und werden nicht im Frontend angezeigt.
            Bei Bedarf bitte Administrator kontaktieren.
          </p>
        </div>
      </Section>

      {/* Changelog */}
      <Section title="Änderungsprotokoll (März 2026)">
        <div className="space-y-3">
          <ChangelogItem date="12.03.2026" changes={[
            "Öffnungszeiten korrigiert (9-12 & 13-18 Uhr)",
            "Adresse korrigiert (Universitätstrasse 25)",
            "Fehlerhafte Inhalte auf Bindeart/Vorschrift/Kontakt behoben",
            "Diese Dokumentation erstellt"
          ]} />
          <ChangelogItem date="11.03.2026" changes={[
            "Navigation von 9 auf 6 Punkte vereinfacht",
            "Weihnachtskarten aus Navigation entfernt",
            "Mobile CSS Probleme behoben",
            "Alle Kategorie-Seiten mit Content aktualisiert"
          ]} />
          <ChangelogItem date="10.03.2026" changes={[
            "Homepage Redesign v2 implementiert",
            "CMS Block Discovery für Kategorie-Landing-Pages",
            "Cache-Flush Workflow optimiert"
          ]} />
        </div>
      </Section>
    </div>
  );
}

// Helper Components
function FeatureCard({ icon, title, description, color }) {
  const colors = {
    amber: 'bg-amber-100 text-amber-600',
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    purple: 'bg-purple-100 text-purple-600',
  };
  
  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition">
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${colors[color]}`}>
        {icon}
      </div>
      <h3 className="font-semibold text-slate-800 mb-2">{title}</h3>
      <p className="text-slate-600 text-sm">{description}</p>
    </div>
  );
}

function Section({ title, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition"
      >
        <h3 className="font-semibold text-slate-800">{title}</h3>
        {isOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
      </button>
      {isOpen && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
}

function StatusItem({ status, title, description }) {
  return (
    <div className="flex items-start gap-3">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
        status === 'done' ? 'bg-emerald-100' : 'bg-slate-100'
      }`}>
        <CheckCircle className={`w-4 h-4 ${status === 'done' ? 'text-emerald-600' : 'text-slate-400'}`} />
      </div>
      <div>
        <h4 className="font-medium text-slate-800">{title}</h4>
        <p className="text-sm text-slate-600">{description}</p>
      </div>
    </div>
  );
}

function TechCard({ title, tech, description }) {
  return (
    <div className="bg-slate-700 rounded-lg p-4">
      <div className="text-slate-400 text-xs uppercase tracking-wide mb-1">{title}</div>
      <div className="text-white font-semibold">{tech}</div>
      <div className="text-slate-400 text-sm mt-1">{description}</div>
    </div>
  );
}

function CodeBlock({ code }) {
  return (
    <pre className="bg-slate-800 text-emerald-400 text-xs p-2 rounded mt-2 overflow-x-auto">
      {code}
    </pre>
  );
}

function APIEndpoint({ method, path, description }) {
  const methodColors = {
    GET: 'bg-blue-100 text-blue-700',
    POST: 'bg-emerald-100 text-emerald-700',
    PUT: 'bg-amber-100 text-amber-700',
    DELETE: 'bg-red-100 text-red-700',
  };
  
  return (
    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
      <span className={`px-2 py-1 rounded text-xs font-mono font-bold ${methodColors[method]}`}>
        {method}
      </span>
      <code className="text-sm font-mono text-slate-700">{path}</code>
      <span className="text-sm text-slate-500">— {description}</span>
    </div>
  );
}

function ChangelogItem({ date, changes }) {
  return (
    <div className="border-l-2 border-emerald-500 pl-4">
      <div className="text-sm font-semibold text-emerald-600 mb-1">{date}</div>
      <ul className="text-sm text-slate-600 space-y-1">
        {changes.map((change, i) => (
          <li key={i}>• {change}</li>
        ))}
      </ul>
    </div>
  );
}

export default Documentation;
